// <pfs-card-mini>: die kompakte „Icon-Card" als geteiltes Primitiv (PFS-705).
//
// Eine kleine Schwester der Standard-<pfs-card> — die kompakte Popover-/Icon-
// Card-Optik (min-width, Uppercase-Header, borderloser Actions-Slot), wie sie
// <pfs-system-status> + der <pfs-notification-inbox>-Dropdown bisher JE INLINE
// nachgebaut haben. Kapselt Header (Titel + actions-Slot) + Body mit
// loading/empty/content-Zuständen (data-state).
//
// Der Reload-MECHANISMUS ist ein eigenes Epic (PFS-706) — pfs-card-mini liefert
// nur die Aufhängung (actions-Slot, z.B. für einen Refresh-Button).
//
// Attribute (alle data-*):
//   data-title          Header-Titel (Uppercase gerendert).
//   data-state          "loading" | "empty" | "ready" (Default "ready").
//   data-empty-title    Text im Empty-Zustand.
//   data-loading-label  Text am Loading-Indicator (i18n; Default "Loading").
//
// Slots:
//   (default)   Card-Inhalt (im "ready"-Zustand sichtbar).
//   actions     Header-Aktionen rechts (Refresh/Buttons).
//
// Sichtbare Default-Strings Englisch; der Consumer lokalisiert via data-*.

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import "../loading-indicator/pfs-loading-indicator.js";

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; min-width: 18rem; }

.card { border: 1px solid var(--pfs-color-outline); border-radius: 0.5rem; background: var(--pfs-color-surface-sunken); overflow: hidden; }
.card__header { display: flex; justify-content: space-between; align-items: center; gap: 1rem; padding: 0.5rem 0.75rem; }
.card__title { font-size: 0.75rem; font-weight: 600; color: var(--pfs-color-on-surface-subtle); text-transform: uppercase; letter-spacing: 0.05em; }
.card__actions { display: inline-flex; align-items: center; gap: 0.4rem; }
.card__actions:empty { display: none; }
.card__body { display: flex; flex-direction: column; gap: 0.6rem; padding: 0.6rem 0.75rem; border-top: 1px solid var(--pfs-color-outline); }

.card__loading { color: var(--pfs-color-on-surface-subtle); }
.card__empty { font-size: 0.8rem; color: var(--pfs-color-on-surface-subtle); }

[hidden] { display: none !important; }
`;

defineComponent({
  tagName: "pfs-card-mini",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;

    const style = document.createElement("style");
    style.textContent = STYLES;

    const card = document.createElement("div");
    card.className = "card";
    card.innerHTML = `
      <div class="card__header">
        <span class="card__title"></span>
        <span class="card__actions"><slot name="actions"></slot></span>
      </div>
      <div class="card__body">
        <pfs-loading-indicator class="card__loading" data-variant="inline" hidden></pfs-loading-indicator>
        <div class="card__empty" hidden></div>
        <div class="card__content"><slot></slot></div>
      </div>`;

    const titleEl = card.querySelector(".card__title") as HTMLElement;
    const loadingEl = card.querySelector(".card__loading") as HTMLElement;
    const emptyEl = card.querySelector(".card__empty") as HTMLElement;
    const contentEl = card.querySelector(".card__content") as HTMLElement;

    root.append(style, card);

    const paint = (): void => {
      titleEl.textContent = host.getAttribute("data-title") ?? "";
      emptyEl.textContent = host.getAttribute("data-empty-title") ?? "";
      loadingEl.setAttribute("data-text", host.getAttribute("data-loading-label")?.trim() || "Loading");

      const state = host.getAttribute("data-state")?.trim() || "ready";
      loadingEl.hidden = state !== "loading";
      emptyEl.hidden = state !== "empty";
      contentEl.hidden = state === "loading" || state === "empty";
    };
    paint();

    const obs = new MutationObserver(paint);
    obs.observe(host, {
      attributes: true,
      attributeFilter: ["data-title", "data-state", "data-empty-title", "data-loading-label"],
    });
    ctx.cleanup.push(() => obs.disconnect());
  },
});
